import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Register from './components/Register';
import PreviewRequired from './components/PreviewRequired';
import JitsiCallView from './components/JitsiCallView';
import {
  AuthScreen,
  ChatsScreen,
  ChatThreadScreen,
  EarningsScreen,
  GuestRateScreen,
  HostTokensScreen,
  HowItWorksScreen,
  InviteConfirmScreen,
  LoungeScreen,
  MatchScreen,
  PostCallScreen,
  PrivacyScreen,
  ProfilePeekScreen,
  ProfileScreen,
  ProfileSetupScreen,
  ScreensIndex,
  SettingsScreen,
  SignupScreen,
  TermsScreen,
  TrustTierScreen,
  VerifyIdScreen,
  WalletScreen,
  WelcomeScreen,
  YoureInScreen,
} from './components/Screens';
import './App.css';
import { API_URL, isProbablyJwt } from './demoMode';

function App() {
  const getInitialPreviewSkipped = () => localStorage.getItem('previewSkipped') === '1';

  const getInitialToken = () => {
    const raw = localStorage.getItem('token');
    if (!raw) return null;
    if (raw === 'null' || raw === 'undefined') return null;
    return raw;
  };

  const [token, setToken] = useState(getInitialToken());
  const isAuthenticated = isProbablyJwt(token);
  const [isCheckingPreview, setIsCheckingPreview] = useState(false);
  const [hasPreview, setHasPreview] = useState(false);
  const [previewSkipped, setPreviewSkipped] = useState(getInitialPreviewSkipped());
  const homePath = '/lounge';

  const handleLogin = (newToken) => {
    if (!newToken || typeof newToken !== 'string') {
      localStorage.removeItem('token');
      setToken(null);
      return;
    }

    localStorage.setItem('token', newToken);
    setToken(newToken);
  };

  const handlePreviewUploaded = () => {
    localStorage.removeItem('previewSkipped');
    setPreviewSkipped(false);
    setHasPreview(true);
  };

  const handlePreviewSkipped = () => {
    localStorage.setItem('previewSkipped', '1');
    setPreviewSkipped(true);
    setHasPreview(true);
  };

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setHasPreview(false);
      setPreviewSkipped(false);
      localStorage.removeItem('previewSkipped');
      return;
    }

    let active = true;
    setIsCheckingPreview(true);

    const loadProfile = async () => {
      try {
        const response = await fetch(`${API_URL}/v1/user/me/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 401) {
          localStorage.removeItem('token');
          if (active) {
            setToken(null);
            setHasPreview(false);
          }
          return;
        }

        const payload = await response.json().catch(() => null);
        if (active) {
          setHasPreview(Boolean(payload?.preview_video_url));
        }
      } catch (_err) {
        if (active) {
          setHasPreview(false);
        }
      } finally {
        if (active) {
          setIsCheckingPreview(false);
        }
      }
    };

    loadProfile();

    return () => {
      active = false;
    };
  }, [isAuthenticated, token]);

  const withPreviewGate = (element) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }

    if (isCheckingPreview) {
      return (
        <div className="figma-root">
          <div className="figma-phone">
            <div className="figma-content">
              <h2 className="figma-section-title">Loading profile...</h2>
              <p className="figma-sub">Checking your preview setup.</p>
            </div>
          </div>
        </div>
      );
    }

    if (!hasPreview && !previewSkipped) {
      return <Navigate to="/preview-required" replace />;
    }

    return element;
  };

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to={homePath} /> : <Login onLogin={handleLogin} />} 
          />
          <Route 
            path="/register" 
            element={isAuthenticated ? <Navigate to={homePath} /> : <Register onLogin={handleLogin} />} 
          />
          <Route
            path="/preview-required"
            element={isAuthenticated ? <PreviewRequired token={token} onPreviewUploaded={handlePreviewUploaded} onPreviewSkipped={handlePreviewSkipped} /> : <Navigate to="/login" />}
          />

          <Route path="/" element={isAuthenticated ? <WelcomeScreen /> : <Navigate to="/login" />} />
          <Route path="/how" element={isAuthenticated ? <HowItWorksScreen /> : <Navigate to="/login" />} />
          <Route path="/auth" element={isAuthenticated ? <AuthScreen /> : <Navigate to="/login" />} />
          <Route path="/signup" element={isAuthenticated ? <SignupScreen /> : <Navigate to="/login" />} />
          <Route path="/verify-id" element={isAuthenticated ? <VerifyIdScreen /> : <Navigate to="/login" />} />
          <Route path="/profile-setup" element={isAuthenticated ? <ProfileSetupScreen /> : <Navigate to="/login" />} />
          <Route path="/host-tokens" element={isAuthenticated ? <HostTokensScreen /> : <Navigate to="/login" />} />
          <Route path="/guest-rate" element={isAuthenticated ? <GuestRateScreen /> : <Navigate to="/login" />} />
          <Route path="/trust-tier" element={isAuthenticated ? <TrustTierScreen /> : <Navigate to="/login" />} />
          <Route path="/youre-in" element={isAuthenticated ? <YoureInScreen /> : <Navigate to="/login" />} />
          <Route path="/lounge" element={withPreviewGate(<LoungeScreen />)} />
          <Route path="/lounge/peek/:id" element={withPreviewGate(<ProfilePeekScreen />)} />
          <Route path="/invite-confirm/:id" element={withPreviewGate(<InviteConfirmScreen />)} />
          <Route path="/call/:id" element={withPreviewGate(<JitsiCallView />)} />
          <Route path="/post-call/:id" element={withPreviewGate(<PostCallScreen />)} />
          <Route path="/match/:id" element={withPreviewGate(<MatchScreen />)} />
          <Route path="/wallet" element={withPreviewGate(<WalletScreen />)} />
          <Route path="/earnings" element={withPreviewGate(<EarningsScreen />)} />
          <Route path="/profile" element={isAuthenticated ? <ProfileScreen /> : <Navigate to="/login" />} />
          <Route path="/settings" element={withPreviewGate(<SettingsScreen />)} />
          <Route path="/privacy" element={withPreviewGate(<PrivacyScreen />)} />
          <Route path="/terms" element={withPreviewGate(<TermsScreen />)} />
          <Route path="/invites" element={<Navigate to="/lounge" replace />} />
          <Route path="/chats" element={withPreviewGate(<ChatsScreen />)} />
          <Route path="/chats/:id" element={withPreviewGate(<ChatThreadScreen />)} />
          <Route path="/screens" element={isAuthenticated ? <ScreensIndex /> : <Navigate to="/login" />} />
          <Route path="/preview" element={<Navigate to="/preview-required" replace />} />
          <Route path="/users" element={<Navigate to="/lounge" replace />} />
          <Route path="*" element={<Navigate to={isAuthenticated ? homePath : '/login'} />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
